import { Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import { SquareButton } from '../../styles/ButtonStyles'
import {
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  KeyboardDoubleArrowDown,
  KeyboardDoubleArrowUp,
} from '@mui/icons-material'
import React, { useState } from 'react'
import { t } from 'i18next'
import dayjs from 'dayjs'
import { useNotification } from '../../store/redux/hooks'
import { PlannedHours, useCopyWorkActivitiesMutation } from '../../store/redux/reducers/scheduleDateSlice'
import CopyConfirmationDialog from '../../components/Dialog/CopyConfirmationDialog'

const MasonryTileCopyHours = ({
  isPlannedHours,
  selectedSections,
  hours,
  reverseHours,
  employeeId,
  copyInto = () => {},
}: {
  isPlannedHours: boolean
  selectedSections: string[]
  hours: PlannedHours
  reverseHours: PlannedHours
  employeeId: string
  copyInto?: (
    newHours: (prevState: PlannedHours) => {
      [p: string]: { from: string; id: string; to: string }[]
    }
  ) => void
}) => {
  const { displayNotification } = useNotification()
  const [copySchedule] = useCopyWorkActivitiesMutation()
  const isDownLg = useMediaQuery(useTheme().breakpoints.down('lg'))

  const getSelectedDates = (selectedSections: string[]): string[] => {
    return selectedSections.map(date => date)
  }

  const isSelectedHoursValid = (
    selectedSections: string[],
    hours: PlannedHours,
    reverseHours: PlannedHours
  ): boolean => {
    if (selectedSections.length === 0) {
      return false
    }

    const isSame = selectedSections.every(
      date =>
        JSON.stringify(hours[date]) === JSON.stringify(reverseHours[date]) ||
        (hours[date].length === 0 && reverseHours[date].length === 0)
    )

    if (isSame) {
      return false
    }

    const dates = getSelectedDates(selectedSections)
    const earliestDate = dates.reduce((a, b) => (dayjs(a).isBefore(dayjs(b)) ? a : b))

    const userTimezone = dayjs.tz.guess()
    const currentDate = dayjs().tz(userTimezone).startOf('day')

    if (!isPlannedHours && dayjs(earliestDate).startOf('day').isBefore(currentDate)) {
      displayNotification({ message: t('page.schedule.copyingFromTrackedError'), type: 'error' })
      return false
    }

    return true
  }
  const copySelectedHours = async () => {
    if (!isSelectedHoursValid(selectedSections, hours, reverseHours)) {
      return
    }

    setIsLoading(true)

    const dates = getSelectedDates(selectedSections)
    const copiedHours: PlannedHours = selectedSections.reduce((acc, date) => {
      acc[date] = hours[date]
      return acc
    }, {} as PlannedHours)

    try {
      await copySchedule({
        employeeId: employeeId,
        dates: dates,
        status: isPlannedHours ? 'PLANNED' : 'TRACKED',
      }).unwrap()

      copyInto(prevState => ({ ...prevState, ...copiedHours }))
    } catch (error) {
      displayNotification({ message: t('page.schedule.copyingError'), type: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  const PlannedHoursArrows = () => {
    return isDownLg ? <KeyboardDoubleArrowDown /> : <KeyboardDoubleArrowRight />
  }

  const TrackedHoursArrows = () => {
    return isDownLg ? <KeyboardDoubleArrowUp /> : <KeyboardDoubleArrowLeft />
  }

  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  return (
    <>
      <Tooltip title={<Typography variant="caption">{t('components.tooltip.moveSelected')}</Typography>}>
        <span>
          <SquareButton
            color="secondary"
            onClick={() => setIsCopyDialogOpen(true)}
            aria-label={t('components.tooltip.moveSelected')}
          >
            {isPlannedHours ? PlannedHoursArrows() : TrackedHoursArrows()}
          </SquareButton>
        </span>
      </Tooltip>
      <CopyConfirmationDialog
        copySelectedHours={copySelectedHours}
        open={isCopyDialogOpen}
        onClose={() => setIsCopyDialogOpen(false)}
        isLoading={isLoading}
      />
    </>
  )
}

export default MasonryTileCopyHours
